import { DATE_FORMAT } from "@/constants/datetime";
import { TOrganizationForm } from "@/features/organization/types/organizationForm";
import { toDateOnly } from "@/features/organization/utils/format";
import { EOrganizationContractStatus } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { validator } from "@/utils/validator";
import { MAX_CHARACTER_INPUT_VALIDATION, MAX_TEXT_INPUT_VALIDATION } from "@/constants/common";

export const schema = validator.object().shape({
  id: validator.number().nullable().label("組織ID（自動生成）").notRequired(),
  name: validator.string().trim().emptyToNull().max(MAX_CHARACTER_INPUT_VALIDATION).label("組織名").required(),
  contractStatus: validator
    .string()
    .isSelection()
    .emptyToNull()
    .label("ステータス")
    .test((_value, ctx) => {
      const message = validateContractStatus(ctx.parent);
      return message ? ctx.createError({ message }) : true;
    })
    .required(),
  contractPlan: validator.string().trim().emptyToNull().label("契約プラン").max(MAX_CHARACTER_INPUT_VALIDATION).notRequired(),
  trialStartDate: validator
    .mixed()
    .isSelection()
    .isDayjs()
    .label("トライアル開始日")
    .isBeforeDate("trialEndDate", { startLabel: "トライアル開始日", endLabel: "トライアル終了日" })
    .notRequired(),
  trialEndDate: validator
    .mixed()
    .isSelection()
    .isDayjs()
    .isAfterDate("trialStartDate", { startLabel: "トライアル開始日", endLabel: "トライアル終了日" })
    .label("トライアル終了日")
    .notRequired(),
  contractStartDate: validator
    .mixed()
    .isSelection()
    .isDayjs()
    .label("契約日")
    .isSameOrAfterDate("trialEndDate", { startLabel: "トライアル終了日", endLabel: "契約日" })
    .isAfterDate("trialStartDate", { startLabel: "トライアル開始日", endLabel: "契約日" })
    .when("contractStatus", {
      is: EOrganizationContractStatus.DURING_CONTRACT,
      then: (schema) => schema.required(),
    })
    .notRequired(),
  contractEndDate: validator
    .mixed()
    .isSelection()
    .isDayjs()
    .isAfterDate("contractStartDate", { startLabel: "契約日", endLabel: "解約日" })
    .label("解約日")
    .notRequired(),
  note: validator.string().trim().emptyToNull().label("備考").notRequired().max(MAX_TEXT_INPUT_VALIDATION),
});

const CONTRACT_STATUS_MESSAGES = {
  trialEnded: "トライアル終了日が過ぎているため、ステータスを「トライアル中」に設定することはできません。",
  trialEndInFuture: "トライアル終了日が未来の日付の場合、ステータスを「契約中」に設定することはできません。トライアル終了日以降に設定してください。",
  trialEndRequired: "トライアル開始日がある場合、トライアル終了日も必要です。",
  contractEndPassed: "解約日を過ぎているため、ステータスを「トライアル中」または「契約中」に設定することはできません。",
  trialContractStartPassed: "契約開始日が本日または過去の日付の場合、「トライアル」ステータスは選択できません。",
  trialCancelledContractStartPassed: "契約開始日が本日または過去の日付の場合、「トライアル終了」ステータスは選択できません。",
} as const;

type TContractStatusFields = Pick<TOrganizationForm, "contractStatus" | "trialStartDate" | "trialEndDate" | "contractStartDate" | "contractEndDate">;

const validateContractStatus = (parent: TContractStatusFields): string | null => {
  const { contractStatus } = parent;
  if (!contractStatus) return null;

  const today = dayjs(dayjs().format(DATE_FORMAT));
  const trialEndDate = toDateOnly(parent.trialEndDate);
  const contractEndDate = toDateOnly(parent.contractEndDate);
  const contractStartDate = toDateOnly(parent.contractStartDate);

  const hasTrialStartDate = parent.trialStartDate !== null && parent.trialStartDate !== undefined;
  const hasTrialEndDate = parent.trialEndDate !== null && parent.trialEndDate !== undefined;

  const isTrial = contractStatus === EOrganizationContractStatus.TRIAL;
  const isDuringContract = contractStatus === EOrganizationContractStatus.DURING_CONTRACT;
  const isTrialCancelled = contractStatus === EOrganizationContractStatus.TRIAL_CANCELLED;

  if (isTrial && trialEndDate && today.isAfter(trialEndDate)) {
    return CONTRACT_STATUS_MESSAGES.trialEnded;
  }

  if (isDuringContract && hasTrialEndDate && trialEndDate && today.isBefore(trialEndDate)) {
    return CONTRACT_STATUS_MESSAGES.trialEndInFuture;
  }

  if (isDuringContract && hasTrialStartDate && !hasTrialEndDate) {
    return CONTRACT_STATUS_MESSAGES.trialEndRequired;
  }

  if ((isTrial || isDuringContract) && contractEndDate && today.isAfter(contractEndDate)) {
    return CONTRACT_STATUS_MESSAGES.contractEndPassed;
  }

  if (isTrial && !hasTrialEndDate && contractStartDate && today.isSameOrAfter(contractStartDate)) {
    return CONTRACT_STATUS_MESSAGES.trialContractStartPassed;
  }

  if (isTrialCancelled && contractStartDate && contractStartDate.isSameOrBefore(today)) {
    return CONTRACT_STATUS_MESSAGES.trialCancelledContractStartPassed;
  }

  return null;
};
